import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Ban, Images, Mail, PlayCircle, Power, TestTube2, Undo2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import LaufButton from './LaufButton';
import { zahl, zeitpunkt } from '../lib/format';
import type {
  AktivierBericht, FtpOrdner, LaufStand, NachlieferBericht, ProtokollZeile,
} from '../lib/typen';

/**
 * Der Schreiblauf — der einzige Ort in diesem Programm, an dem etwas in PlentyMarkets
 * entsteht.
 *
 * 🔴 Es gibt keine Plenty-Sandbox: auch von einer Dev-Instanz aus landen die Artikel im
 * echten Shop (E31). Deshalb steht vor dem Knopf eine Tippbestätigung, keine Checkbox —
 * ein versehentlicher Klick soll keine 241 Artikel erzeugen.
 *
 * Der Lauf arbeitet im Hintergrund weiter; diese Ansicht fragt den Stand ab. Das
 * Polling läuft nur, solange der Lauf läuft, und hört von selbst auf.
 */
export default function SchreiblaufPanel({
  entwurfId, ausgewaehlt, bildOrdner, artikel, berichtEmpfaenger,
}: {
  entwurfId: number;
  ausgewaehlt: number;
  /** Die FTP-Ordner zur Auswahl fuer „Bilder nachliefern". */
  bildOrdner: FtpOrdner[];
  /** Die auswählbaren Artikel — für den Einzelartikel-Testlauf. */
  artikel: Array<{ artikelSchluessel: string; name: string; varianten: number; ausgewaehlt: boolean }>;
  berichtEmpfaenger: string | null;
}) {
  const [bestaetigung, setBestaetigung] = useState('');
  const [laufId, setLaufId] = useState<number | null>(null);
  const [stand, setStand] = useState<LaufStand | null>(null);
  const [protokoll, setProtokoll] = useState<ProtokollZeile[]>([]);
  const [aktivierung, setAktivierung] = useState<AktivierBericht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [startet, setStartet] = useState(false);
  const [nachlieferOrdner, setNachlieferOrdner] = useState('');
  const [nachliefert, setNachliefert] = useState(false);
  const [nachlieferung, setNachlieferung] = useState<NachlieferBericht | null>(null);
  const [testArtikel, setTestArtikel] = useState('');
  /**
   * Wie viele Artikel dieser Lauf vorhat. Der Stand vom Server zaehlt nur nach oben
   * („angelegt"), die Gesamtzahl kennt nur die Seite, die den Lauf gestartet hat —
   * ohne sie gaebe es keinen Prozentwert, sondern nur eine wachsende Zahl.
   */
  const [geplant, setGeplant] = useState<number | null>(null);
  const [empfaenger, setEmpfaenger] = useState(berichtEmpfaenger ?? '');
  const [empfaengerStand, setEmpfaengerStand] = useState<string | null>(null);
  const [berichtStand, setBerichtStand] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const standHolen = useCallback(async (id: number) => {
    try {
      const antwort = await api.get<{ data: LaufStand }>(`/programmes/artikelimport/laeufe/${id}`);
      setStand(antwort.data.data);
      if (antwort.data.data.status !== 'laufend' && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
        const p = await api.get<{ data: ProtokollZeile[] }>(
          `/programmes/artikelimport/laeufe/${id}/protokoll`,
        );
        setProtokoll(p.data.data);
      }
    } catch {
      // Ein verlorener Poll ist kein Grund, die Ansicht zu leeren — beim nächsten
      // Durchgang steht der Stand wieder da.
    }
  }, []);

  useEffect(() => () => {
    if (timer.current) clearInterval(timer.current);
  }, []);

  /**
   * 🔑 Der Einzelartikel-Testlauf (§13, JAKO-Muster) — der kleinste scharfe Schritt.
   *
   * `nurArtikel` filtert den Lauf, ohne die Auswahl im Entwurf anzufassen: wer einen
   * Artikel zur Probe anlegt, will danach nicht 240 neu anhaken. Was angelegt ist,
   * hakt der Lauf selbst ab — ein späterer großer Lauf legt es kein zweites Mal an.
   */
  const starten = useCallback(async (nurArtikel?: string[]) => {
    setStartet(true);
    setGeplant(nurArtikel ? nurArtikel.length : ausgewaehlt);
    setFehler(null);
    setProtokoll([]);
    setAktivierung(null);
    try {
      const antwort = await api.post<{ data: {
        laufId: number;
        fehlendeAttributwerte: string[];
        nichtAnlegbar?: Array<{ artikel: string; name: string; gruende: string[] }>;
      } }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/schreiben`,
        { bestaetigung: 'ANLEGEN', ...(nurArtikel ? { nurArtikel } : {}) },
      );
      const id = antwort.data.data.laufId;
      setLaufId(id);
      // 🔴 Artikel, die Plenty nicht unterscheiden könnte, sind gar nicht erst im Lauf.
      // Das muss oben stehen, nicht in den Logs: sonst wundert sich jemand, warum von
      // 90 ausgewählten Artikeln nur 30 angelegt wurden.
      const nichtAnlegbar = antwort.data.data.nichtAnlegbar ?? [];
      if (nichtAnlegbar.length > 0) {
        setFehler(
          `${nichtAnlegbar.length} Artikel werden übersprungen — Plenty könnte ihre `
          + `Varianten nicht auseinanderhalten (meist fehlt der Farb- oder Größenwert). `
          + `Zum Beispiel: ${nichtAnlegbar.slice(0, 3).map(a => a.name).join(', ')}`
          + `${nichtAnlegbar[0]?.gruende?.[0] ? ` — ${nichtAnlegbar[0].gruende[0]}` : ''}`,
        );
      } else if (antwort.data.data.fehlendeAttributwerte.length > 0) {
        setFehler(
          `${antwort.data.data.fehlendeAttributwerte.length} Attributwerte fehlen in Plenty — `
          + `die betroffenen Varianten lassen sich damit nicht anlegen.`,
        );
      }
      await standHolen(id);
      timer.current = setInterval(() => void standHolen(id), 3000);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Start fehlgeschlagen.');
    } finally {
      setStartet(false);
      setBestaetigung('');
    }
  }, [entwurfId, standHolen, ausgewaehlt]);

  const empfaengerSpeichern = useCallback(async () => {
    try {
      await api.patch(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/bericht-empfaenger`,
        { empfaenger },
      );
      setEmpfaengerStand('Gespeichert.');
    } catch (err: any) {
      setEmpfaengerStand(err?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.');
    }
  }, [entwurfId, empfaenger]);

  const berichtSenden = useCallback(async () => {
    if (!laufId) return;
    try {
      const antwort = await api.post<{ data: { gesendet: boolean; empfaenger: string[]; grund?: string } }>(
        `/programmes/artikelimport/laeufe/${laufId}/bericht`, {},
      );
      const d = antwort.data.data;
      setBerichtStand(d.gesendet
        ? `Bericht an ${d.empfaenger.join(', ')} verschickt.`
        : d.grund ?? 'Der Bericht ging nicht raus.');
    } catch (err: any) {
      setBerichtStand(err?.response?.data?.error?.message ?? 'Versand fehlgeschlagen.');
    }
  }, [laufId]);

  const abbrechen = useCallback(async () => {
    if (!laufId) return;
    await api.post(`/programmes/artikelimport/laeufe/${laufId}/abbrechen`, {});
    await standHolen(laufId);
  }, [laufId, standHolen]);

  const aktivieren = useCallback(async () => {
    if (!laufId) return;
    const antwort = await api.post<{ data: AktivierBericht }>(
      `/programmes/artikelimport/laeufe/${laufId}/aktivieren`, {},
    );
    setAktivierung(antwort.data.data);
  }, [laufId]);

  /**
   * 🔴 Bilder nachliefern — rein additiv, aber es landen echte Dateien in Plenty.
   *
   * Der Ordner ist bewusst nicht vorbelegt: bei uhlsport liegen die Fotos auf zwei
   * Ordner verteilt, die keine Materialnummer gemeinsam haben. Welcher hier gemeint
   * ist, weiss nur der Anwender.
   */
  const bilderNachliefern = useCallback(async () => {
    if (!laufId || !nachlieferOrdner) return;
    setNachliefert(true);
    setFehler(null);
    try {
      const antwort = await api.post<{ data: NachlieferBericht }>(
        `/programmes/artikelimport/laeufe/${laufId}/bilder`,
        { ordner: nachlieferOrdner, bestaetigung: 'BILDER' },
      );
      setNachlieferung(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Nachliefern fehlgeschlagen.');
    } finally {
      setNachliefert(false);
    }
  }, [laufId, nachlieferOrdner]);

  const zuruecknehmen = useCallback(async () => {
    if (!laufId) return;
    const antwort = await api.post<{ data: AktivierBericht }>(
      `/programmes/artikelimport/laeufe/${laufId}/rollback`,
      { bestaetigung: 'DEAKTIVIEREN' },
    );
    setAktivierung(antwort.data.data);
  }, [laufId]);

  const laeuft = stand?.status === 'laufend';
  const fertig = stand != null && !laeuft;

  return (
    <section className="space-y-4 rounded border-2 border-red-300 bg-red-50/30 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" />
        <div>
          <h2 className="font-semibold text-[#1A1A1A]">Artikel in PlentyMarkets anlegen</h2>
          <p className="mt-1 text-sm text-[#1A1A1A]/80">
            Das hier schreibt wirklich. Es gibt keine Plenty-Testumgebung — auch von
            dieser Instanz aus landen die Artikel im echten Shop. Sie entstehen
            <strong> inaktiv</strong> und gehen erst mit „Aktivieren" live.
          </p>
        </div>
      </div>

      {fehler && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {fehler}
        </div>
      )}

      {!laufId && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-[#1A1A1A]/70">
              Zum Bestätigen <strong>ANLEGEN</strong> tippen
            </span>
            <input
              type="text"
              value={bestaetigung}
              onChange={e => setBestaetigung(e.target.value)}
              placeholder="ANLEGEN"
              className="rounded border border-[#DAD9D9] px-3 py-2 font-mono"
            />
          </label>
          <LaufButton
            laeuft={startet}
            laufText="Lauf wird gestartet"
            icon={<PlayCircle className="mr-2 h-4 w-4" />}
            onClick={() => void starten()}
            disabled={bestaetigung !== 'ANLEGEN' || ausgewaehlt === 0}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {`${zahl(ausgewaehlt)} Artikel anlegen`}
          </LaufButton>
        </div>
      )}

      {!laufId && (
        <div className="space-y-2 rounded border border-[#DAD9D9] bg-white p-3">
          <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
            <TestTube2 className="h-4 w-4" /> Erst einen einzelnen Artikel
          </h3>
          <p className="text-xs text-[#1A1A1A]/70">
            Der kleinste scharfe Schritt: EIN Artikel geht nach Plenty, danach schaut
            jemand hinein. Die Auswahl bleibt, wie sie ist — der angelegte Artikel
            hakt sich selbst ab und wird im großen Lauf nicht noch einmal angelegt.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={testArtikel}
              onChange={e => setTestArtikel(e.target.value)}
              className="max-w-md flex-1 rounded border border-[#DAD9D9] px-3 py-2 text-sm"
            >
              <option value="">Artikel wählen …</option>
              {artikel.filter(a => a.ausgewaehlt).map(a => (
                <option key={a.artikelSchluessel} value={a.artikelSchluessel}>
                  {a.name} ({zahl(a.varianten)} Varianten)
                </option>
              ))}
            </select>
            <LaufButton
              variant="outline"
              size="sm"
              laeuft={startet}
              laufText="Artikel wird angelegt"
              onClick={() => void starten([testArtikel])}
              disabled={!testArtikel}
            >
              Diesen einen anlegen
            </LaufButton>
          </div>
        </div>
      )}

      <div className="space-y-2 rounded border border-[#DAD9D9] bg-white p-3">
        <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
          <Mail className="h-4 w-4" /> Abschlussbericht
        </h3>
        <p className="text-xs text-[#1A1A1A]/70">
          Geht nach jedem Lauf automatisch an dich — mit dem vollständigen Protokoll als
          CSV. Hier kann eine weitere Adresse dazukommen (mehrere durch Komma getrennt).
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={empfaenger}
            onChange={e => { setEmpfaenger(e.target.value); setEmpfaengerStand(null); }}
            placeholder="zusätzlich an … (optional)"
            className="w-72 rounded border border-[#DAD9D9] px-3 py-2 text-sm"
          />
          <Button variant="outline" size="sm" onClick={() => void empfaengerSpeichern()}>
            Speichern
          </Button>
          {laufId && (
            <Button variant="outline" size="sm" onClick={() => void berichtSenden()}>
              Bericht erneut senden
            </Button>
          )}
        </div>
        {empfaengerStand && <p className="text-xs text-[#1A1A1A]/60">{empfaengerStand}</p>}
        {berichtStand && <p className="text-xs text-[#1A1A1A]/60">{berichtStand}</p>}
      </div>

      {stand && (
        <div className="space-y-3 rounded border border-[#DAD9D9] bg-white p-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium text-[#1A1A1A]">Lauf {stand.id}</span>
            <span className={`rounded px-2 py-0.5 text-xs ${
              stand.status === 'laufend' ? 'bg-[#E9FF57] text-[#1A1A1A]'
              : stand.status === 'fertig' ? 'bg-green-50 text-green-900'
              : stand.status === 'abgebrochen' ? 'bg-amber-50 text-amber-900'
              : 'bg-red-50 text-red-900'
            }`}>
              {stand.status}
            </span>
            <span className="tabular-nums text-[#1A1A1A]/70">
              {zahl(stand.angelegt)} angelegt
              {stand.fehler > 0 && ` · ${zahl(stand.fehler)} Fehler`}
            </span>
            <FolgeStand stand={stand} />
            <BilderStand stand={stand} />
            <span className="text-[#1A1A1A]/50">seit {zeitpunkt(stand.gestartetAm)}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {laeuft && (
              <Button variant="outline" size="sm" onClick={() => void abbrechen()}>
                <Ban className="mr-2 h-3.5 w-3.5" />
                {stand.abbruchAngefordert ? 'Abbruch angefordert …' : 'Abbrechen'}
              </Button>
            )}
            {fertig && stand.angelegt > 0 && (
              <>
                <Button
                  size="sm"
                  onClick={() => void aktivieren()}
                  className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
                >
                  <Power className="mr-2 h-3.5 w-3.5" />
                  Aktivieren
                </Button>
                <Button variant="outline" size="sm" onClick={() => void zuruecknehmen()}>
                  <Undo2 className="mr-2 h-3.5 w-3.5" />
                  Alle wieder deaktivieren
                </Button>
              </>
            )}
          </div>

          {laeuft && (
            <>
              {/* Echter Zaehler: angelegt + Fehler von den geplanten Artikeln. Der
                  Balken erscheint nur, wenn die Gesamtzahl bekannt ist — nach einem
                  Seitenwechsel ist sie das nicht, und dann steht hier lieber die
                  nackte Zahl als ein geratener Prozentwert. */}
              {geplant != null && geplant > 0 && (
                <div className="max-w-md space-y-1">
                  <div className="h-2 overflow-hidden rounded bg-[#DAD9D9]">
                    <div
                      className="h-full rounded bg-[#E9FF57] transition-all"
                      style={{ width: `${Math.min(100, ((stand!.angelegt + stand!.fehler) / geplant) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs tabular-nums text-[#1A1A1A]/70">
                    {zahl(stand!.angelegt + stand!.fehler)} von {zahl(geplant)} Artikeln
                    {' · '}
                    {Math.round(((stand!.angelegt + stand!.fehler) / geplant) * 100)} %
                  </p>
                </div>
              )}
              <p className="text-xs text-[#1A1A1A]/60">
                Der Lauf arbeitet im Hintergrund weiter, auch wenn du diese Seite verlässt.
                Ein Abbruch hält nach dem aktuellen Artikel an — was angelegt ist, bleibt.
              </p>
            </>
          )}
        </div>
      )}

      {fertig && stand!.angelegt > 0 && (
        <div className="space-y-2 rounded border border-[#DAD9D9] bg-white p-3">
          <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
            <Images className="h-4 w-4" /> Bilder nachliefern
          </h3>
          <p className="text-xs text-[#1A1A1A]/70">
            Holt fehlende Fotos zu den Artikeln dieses Laufs. Rein zusätzlich — es wird
            kein Bild gelöscht und kein Feld angefasst. Was schon hochgeladen wurde,
            wird übersprungen. Die uhlsport-Ordner ergänzen sich, hier lässt sich also
            gezielt der zweite ziehen.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={nachlieferOrdner}
              onChange={e => setNachlieferOrdner(e.target.value)}
              className="rounded border border-[#DAD9D9] px-3 py-2 text-sm"
            >
              <option value="">Ordner wählen …</option>
              {bildOrdner.filter(o => o.bildAnzahl > 0).map(o => (
                <option key={o.pfad} value={o.pfad}>
                  {o.name} ({zahl(o.bildAnzahl)} Bilder)
                </option>
              ))}
            </select>
            <LaufButton
              size="sm"
              variant="outline"
              laeuft={nachliefert}
              laufText="Bilder werden nachgeliefert"
              dauerHinweis="ein Upload je Farbe, eine Verknüpfung je Variante"
              onClick={() => void bilderNachliefern()}
              disabled={!nachlieferOrdner}
            >
              Nachliefern
            </LaufButton>
          </div>
          {nachlieferung && (
            <div className="text-sm text-[#1A1A1A]/80">
              <p>
                {zahl(nachlieferung.hochgeladen)} Bilder hochgeladen,{' '}
                {zahl(nachlieferung.verknuepft)}× verknüpft
                {nachlieferung.schonVerknuepft > 0
                  && ` · ${zahl(nachlieferung.schonVerknuepft)} hingen schon`}
                {nachlieferung.uebersprungen > 0
                  && ` · ${zahl(nachlieferung.uebersprungen)} übersprungen`}
                {nachlieferung.fehler > 0 && ` · ${zahl(nachlieferung.fehler)} Fehler`}{' '}
                — bei {zahl(nachlieferung.artikelMitNachschub)} von{' '}
                {zahl(nachlieferung.artikelBetrachtet)} Artikeln.
              </p>
              {nachlieferung.meldungen.map((m, i) => (
                <p key={i} className="mt-1 text-xs text-amber-800">{m}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {aktivierung && (
        <div className="rounded border border-[#DAD9D9] bg-white p-3 text-sm">
          <p className="text-[#1A1A1A]/80">
            {zahl(aktivierung.aktiviert)} Varianten aktiviert
            {aktivierung.bleibtInaktiv > 0 && `, ${zahl(aktivierung.bleibtInaktiv)} bleiben aus`}
            {aktivierung.fehler > 0 && ` · ${zahl(aktivierung.fehler)} Fehler`}.
          </p>
          {aktivierung.meldungen.map((m, i) => (
            <p key={i} className="mt-1 text-xs text-[#1A1A1A]/60">{m}</p>
          ))}
        </div>
      )}

      {protokoll.length > 0 && (
        <details className="rounded border border-[#DAD9D9] bg-white p-3">
          <summary className="cursor-pointer text-sm font-medium text-[#1A1A1A]">
            Protokoll ({zahl(protokoll.length)} Zeilen)
          </summary>
          <div className="mt-2 max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody>
                {protokoll.map((z, i) => (
                  <tr key={i} className="border-b border-[#DAD9D9]/50">
                    <td className="py-1 pr-3">{z.artikel}</td>
                    <td className="py-1 pr-3 text-[#1A1A1A]/60">{z.variante ?? 'Hauptvariante'}</td>
                    <td className={`py-1 pr-3 ${z.status === 'fehler' ? 'text-red-700' : ''}`}>
                      {z.status}
                    </td>
                    <td className="py-1 pr-3 tabular-nums text-[#1A1A1A]/60">
                      {z.plentyItemId ?? ''}
                    </td>
                    <td className="py-1 text-[#1A1A1A]/70">{z.meldung ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}

/**
 * Was der Lauf mit den Bildern gemacht hat.
 *
 * Steht nur da, wenn ein Bildordner gewählt war — sonst wurde nichts versucht, und
 * eine Null wäre eine falsche Aussage.
 */
function BilderStand({ stand }: { stand: LaufStand }) {
  const bilder = (stand.zusammenfassung as any)?.bilder;
  if (!bilder) return null;
  return (
    <span className="tabular-nums text-[#1A1A1A]/70">
      {zahl(bilder.hochgeladen ?? 0)} Bilder, {zahl(bilder.verknuepft ?? 0)}× verknüpft
      {(bilder.fehler ?? 0) > 0 && ` · ${zahl(bilder.fehler)} Bildfehler`}
    </span>
  );
}

/**
 * Was der Lauf an Eigenschaften und Tags gesetzt hat.
 *
 * Die Zahl der Schreibvorgänge steht bewusst dabei: sie ist der Unterschied zwischen
 * einem Lauf von Minuten und einem von Stunden.
 */
function FolgeStand({ stand }: { stand: LaufStand }) {
  const folge = (stand.zusammenfassung as any)?.folge;
  if (!folge) return null;
  return (
    <span
      className="tabular-nums text-[#1A1A1A]/70"
      title={`${zahl(folge.calls ?? 0)} Schreibvorgänge — einzeln wären es `
        + `${zahl((folge.eigenschaften ?? 0) + (folge.tags ?? 0))}.`}
    >
      {zahl(folge.eigenschaften ?? 0)} Eigenschaften, {zahl(folge.tags ?? 0)} Tag-Sätze
      {(folge.eigenschaftenFehler ?? 0) + (folge.tagsFehler ?? 0) > 0
        && ` · ${zahl((folge.eigenschaftenFehler ?? 0) + (folge.tagsFehler ?? 0))} Fehler`}
    </span>
  );
}
